import React from 'react';
import moment from 'moment';
import ForexCrossSellingAmountInput from '../components/ForexCrossSellingAmountInput';
import {
  NIUM_FOREX_CROSS_SELL_FORM_NAME,
  NIUM_FOREX_CROSS_SELL_PRODUCTS,
  NIUM_FOREX_CROSS_SELL_PROPOSITION,
  niumForexCrossSellFields
} from './constants';
import { change, Field } from 'redux-form';
import { getContentSchemaFromContentFul } from '../../../../../redux/actions/common/common-action';
import {
  changeNiumForexScreen,
  registerCrossSellingInterest,
  setCrossSellingInformationAction
} from '../../../../../redux/actions/nium-forex/nium-forex-action';
import { CrossSellingFormType, CrossSellingTabsType, UserEventPayLoadType } from './types';
import { EventNames, FLOW_TYPE, LOG_TYPE } from '../../../../../utils/events/eventsConstants';
import { AppDispatch } from '../../../../../store.type';
import {
  activateLoader,
  disableLoader
} from '../../../../../redux/actions/ui-trigger/ui-trigger-action';
import { NF_SCREENS } from '../../../root/screenConstants';
import { logEvent } from '../../../../../utils/events/logUserEvents';

export const renderFormFields = (formValues: any, props: any, crossSellingState: any) => {
  return niumForexCrossSellFields(formValues, props, crossSellingState).map((field: any) => {
    if (field.name === 'forex_amount')
      return <ForexCrossSellingAmountInput key={field.name} {...field} />;
    return <Field key={field.name} {...field} />;
  });
};

export const getContentForCrossSelling = async (dispatch: any) => {
  const response = await getContentSchemaFromContentFul('module', 'NIUM_FOREX_CROSS_SELL');
  const transFormedData = transformData(response);
  if (response.labels) {
    dispatch(
      setCrossSellingInformationAction({
        labels: transFormedData.labels,
        explore: transFormedData.explore,
        components: transFormedData.components
      })
    );
  }
};

export const logUserEventForNFCrossSelling = ({
  pageName,
  eventName,
  properties = {}
}: UserEventPayLoadType) => {
  const payLoad = { ...properties, pageName };
  const { baseName } = EventNames.NF_CROSS_SELLING;
  logEvent(`${baseName}${eventName}`, FLOW_TYPE.NF_CROSS_SELLING, payLoad, LOG_TYPE.CLEVER_TAP);
};

const transformData = (data: any) => {
  if (Object.keys(data || {}).length > 0) {
    const resultantData: any = {};
    resultantData.explore = {};
    resultantData.labels = {};
    const {
      configurations: {
        value: { tabs }
      },
      labels: { value: labels },
      screens,
      components
    } = data;

    const transformedTabs = tabs?.map(({ name, title }: CrossSellingTabsType) => ({
      name: name.toLocaleLowerCase(),
      title
    }));

    const nfScreens = transformScreensAndCards(screens.value);
    const transFormedComponentsArray = transformScreensAndCards(components.value);
    let transFormedComponentsObject = {};
    for (const comp of transFormedComponentsArray) {
      transFormedComponentsObject = { ...transFormedComponentsObject, ...comp };
    }
    resultantData['explore']['tabs'] = transformedTabs;

    resultantData['explore']['screens'] = nfScreens[0];
    resultantData['labels'] = labels;
    resultantData['components'] = transFormedComponentsObject;

    return resultantData;
  }
  return NIUM_FOREX_CROSS_SELL_PROPOSITION;
};

/**
 *
 * @param dataArray {any[]} array of data
 * @param result {Object} result object with relevant keys and values
 * @returns array containing an object  with each screen and all the information required to render each tab
 *
 * Recursive function to transform CMS data to a form that's required to render tab information
 * Transforms the screens first and then recursively transforms the cards for each screen and appends the cards data to
 * respective parent object
 */

const transformScreensAndCards = (
  dataArray: any[],
  res: any = {},
  shouldConvertToObject = true,
  keyToInsert = 'cards'
) => {
  const data = dataArray.map((data) => {
    const key = data?.target?.fields?.title?.value?.toLowerCase();
    const description = data?.target?.fields?.description?.value;
    const title = data?.target?.fields?.headerText?.value;
    const icon = data?.target?.fields?.icon?.target?.fields?.light?.target?.fields?.url;
    const components = data?.target?.fields?.components?.value;
    const buttonText = data?.target?.fields?.primaryButton?.target?.fields?.label?.value;
    const labels = data?.target?.fields?.labels?.value;
    // case for screen
    if (shouldConvertToObject) {
      res[key] = {
        key,
        description,
        title
      };
    } else {
      res = {
        key,
        description,
        title
      };
    }

    if (components) {
      res[key][keyToInsert] = transformScreensAndCards(components, {}, false);
    }
    if (icon) {
      if (shouldConvertToObject) res[key]['icon'] = `https:${icon}`;
      else res.icon = `https:${icon}`;
    }
    if (buttonText) {
      if (shouldConvertToObject) res[key]['buttonText'] = buttonText;
      else res.buttonText = buttonText;
    }
    if (labels) {
      if (shouldConvertToObject) res[key]['labels'] = labels;
      else res.labels = labels;
    }

    return res;
  });
  return data;
};

export const handleCheckboxChange = (
  dispatch: AppDispatch,
  crossSellingState: any,
  formValues: any
) => {
  dispatch(
    change(NIUM_FOREX_CROSS_SELL_FORM_NAME, 'is_forex_applied', !formValues['is_forex_applied'])
  );
  const { selectedProducts, currentExploreTab } = crossSellingState;
  const otherProduct = NIUM_FOREX_CROSS_SELL_PRODUCTS.find(
    (product: string) => product.toLocaleLowerCase() !== currentExploreTab
  )?.toUpperCase();

  let result = selectedProducts;
  if (formValues['is_forex_applied']) {
    result = result.filter((product: string) => product !== otherProduct);
  } else {
    result.push(otherProduct?.toUpperCase());
  }

  dispatch(setCrossSellingInformationAction({ selectedProducts: result }));
};
export const handleSubmit = async (
  values: CrossSellingFormType,
  dispatch: AppDispatch,
  selectedProducts: Array<string>
) => {
  const { forex_amount, forex_currency, city, travel_date, contact_number, email } = values;
  const payload = {
    forex_amount: parseInt(forex_amount),
    forex_currency,
    city: city.toString(),
    travel_date: moment(travel_date, 'DD/MM/YYYY').toDate(),
    contact_number,
    email,
    product: selectedProducts
  };
  dispatch(activateLoader());
  const response = await dispatch(registerCrossSellingInterest(payload));
  dispatch(disableLoader());
  if (response.success) {
    logUserEventForNFCrossSelling({
      pageName: NF_SCREENS.cross_selling_form,
      eventName: EventNames.NF_CROSS_SELLING.interestSubmission,
      properties: { selectedProducts }
    });
    dispatch(changeNiumForexScreen(NF_SCREENS.cross_selling_success));
  }
};
